import { ArrowDown, ChevronLeft, ChevronRight, Eye, RefreshCcw } from 'lucide-react';
import { useState } from 'react';
import './EndpointStatusTable.css';
import RightModelHealth from './RightModelHealth';
import { useAzureData } from '../../context/AzureContext';

type Header = {
  name: string;
  sortingAllowed: boolean;
  type: string;
};

type Props = {
  header: Header[];
  data: any[];
  itemOptions?: number[];
  fetchRefreshSingleEnv: any;
};

function getStatusClass(status: number) {
  if (status === 200) return 'status-green';
  if (status >= 300 && status <= 399) return 'status-yellow';
  if (status >= 400 && status <= 499) return 'status-orange';
  if (status >= 500 && status <= 599) return 'status-red';
  return 'status-default';
}

function renderCell(type: string, value: any) {
  // if (type === 'status') {
  if (type === 'status_code') {
    return (
      // <span className={`status-pill status-${value}`}>
      <span className={`status-pill ${getStatusClass(value)}`}>{value}</span>
    );
  }

  if (type === 'risk') {
    return <span className={value === 'Yes' ? 'risk-yes' : 'risk-no'}>{value}</span>;
  }

  if (type === 'response_summary') {
    return <div className="response-summary-cell">{value ?? '-'}</div>;
  }

  return value ?? '-';
}

export default function EndpointStatusTable({
  header,
  data,
  itemOptions = [5, 10],
  fetchRefreshSingleEnv
}: Props) {
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(itemOptions[0]);
  const [sortKey, setSortKey] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [show, setShow] = useState(false);
  const [selectedTableData, setSelectedTableData] = useState<any>();
  const { tempAdmin } = useAzureData();
  const totalPages = Math.ceil(data.length / itemsPerPage);

  const sortedData = [...data].sort((a, b) => {
    if (!sortKey) return 0;
    const aVal = a[sortKey];
    const bVal = b[sortKey];
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const pageData = sortedData.slice((pageNumber - 1) * itemsPerPage, pageNumber * itemsPerPage);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  return (
    <>
      <div className="endpoint-table-card">
        <table className="endpoint-table">
          <thead>
            <tr>
              {header.map((h) => (
                <th key={h.type}>
                  <span className="th-content">
                    {h.name}
                    {h.sortingAllowed && (
                      <ArrowDown
                        size={14}
                        className={`sort-icon ${
                          sortKey === h.type ? 'active' : ''
                        } ${sortOrder === 'desc' ? 'desc' : ''}`}
                        onClick={() => handleSort(h.type)}
                      />
                    )}
                  </span>
                </th>
              ))}
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {pageData.map((row, idx) => (
              <tr key={idx}>
                {header.map((h) => (
                  <td key={h.type}>{renderCell(h.type, row[h.type])}</td>
                ))}
                <td className="action-cell">
                  {tempAdmin && (
                    <RefreshCcw
                      size={16}
                      onClick={() => fetchRefreshSingleEnv(row?.environment, row?.id)}
                    />
                  )}
                  <Eye
                    size={16}
                    onClick={() => {
                      setShow(true);
                      setSelectedTableData(row);
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>

          <tfoot style={{ width: '100%' }}>
            <tr style={{ height: '64px' }}>
              <td
                colSpan={header?.length + 1}
                style={{
                  verticalAlign: 'middle',
                  backgroundColor: 'rgba(235, 242, 254, 1)',
                  width: '200px'
                }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                  <div>
                    <span style={{ color: 'rgba(113, 113, 122, 1)' }}>Total: </span> {data?.length}
                  </div>
                  <div>
                    {' '}
                    <ChevronLeft
                      style={{
                        cursor: 'pointer',
                        color: pageNumber === 1 ? 'rgba(113, 113, 122, 1)' : 'var(--uni_std_clr)',
                        width: '16px'
                      }}
                      onClick={() => {
                        pageNumber !== 1 && setPageNumber(pageNumber - 1);
                      }}
                    />
                    <span
                      style={{
                        width: '50px',
                        borderBottom: '1px solid var(--uni_std_clr)',
                        color: 'var(--uni_std_clr)',
                        margin: '0 20px',
                        padding: '0 10px'
                      }}>
                      {pageNumber}{' '}
                    </span>
                    of{' '}
                    <span
                      style={{
                        width: '50px',
                        margin: '0 20px',
                        padding: '0 10px'
                      }}>
                      {totalPages}{' '}
                    </span>
                    <ChevronRight
                      style={{
                        cursor: 'pointer',
                        color:
                          pageNumber === totalPages
                            ? 'rgba(113, 113, 122, 1)'
                            : 'var(--uni_std_clr)'
                      }}
                      onClick={(prev) => {
                        pageNumber !== totalPages && setPageNumber(pageNumber + 1);
                      }}
                    />
                  </div>
                  <div>
                    {' '}
                    <form action="">
                      <label htmlFor="pages" style={{ color: 'rgba(113, 113, 122, 1)' }}>
                        Show Per Page:
                      </label>
                      <select
                        name="pages"
                        id="pages"
                        style={{
                          border: 'none',
                          backgroundColor: 'rgba(235, 242, 254, 1)',
                          cursor: 'pointer'
                        }}
                        onChange={(e) => setItemsPerPage(parseInt(e.target.value))}>
                        {itemOptions?.map((pageItems, index) => (
                          <option value={pageItems} key={pageItems + index}>
                            {pageItems}
                          </option>
                        ))}
                      </select>
                    </form>
                  </div>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <RightModelHealth
        show={show}
        onClose={() => {
          setShow(false);
          setSelectedTableData(null);
        }}
        data={selectedTableData}
      />
    </>
  );
}
